import { useCallback } from 'react';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type { ConnectorPromoConfig, PromoDismissed } from '../connector-promo-config-types.ts';
import { getRecommendedConnectors } from '../utils/getRecommendedConnectors.ts';

import { useConnectorOnboardingTeamType } from './useConnectorOnboardingTeamType.ts';
import { useConnectorStates } from './useConnectorStates.ts';
import { useSetupConnector } from './useSetupConnector.ts';

const DISABLED_CONFIG: ConnectorPromoConfig = {
	recommendedConnectors: [],
	spotlightShown: false,
	enrolmentDate: null,
	shouldShowEmptyState: false,
	shouldShowNthPromo: false,
	shouldShowNthPagePromo: false,
	shouldShowPostConnectPromo: false,
	shouldShowPostConnectPagePromo: false,
	shouldShowSidebarItem: false,
	shouldShowSpotlight: false,
	onSetupConnector: () => {},
	onDismissPromo: () => {},
	onMaybeLater: () => {},
	onDismissSpotlight: () => {},
};

export function useConnectorPromoConfig({
	hasRecentItems = false,
}: {
	hasRecentItems?: boolean;
} = {}): ConnectorPromoConfig {
	// No experiment check here. Surface components gate rendering via
	// useIsConnectorOnboardingEnabled() before calling this hook.
	const { teamType } = useConnectorOnboardingTeamType({
		skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	});
	const connectorStates = useConnectorStates();

	const { openSetupModal } = useSetupConnector();

	// Stable callback refs — must be declared before any early returns to satisfy rules-of-hooks
	const onSetupConnector = useCallback(
		(connectorKey: string) => openSetupModal(connectorKey),
		[openSetupModal],
	);
	const onDismissPromo = useCallback(() => {}, []);
	const onMaybeLater = useCallback(() => {}, []);
	const onDismissSpotlight = useCallback(() => {}, []);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE,
			TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN,
			TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED,
		],
		!fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	);

	if (onboardingStateError) {
		if (isUnauthorizedError(onboardingStateError)) {
			markErrorAsHandled(onboardingStateError);
		}
		// For non-unauthorized errors, we still degrade gracefully by hiding the promo,
		// but we do NOT call markErrorAsHandled so the error remains visible in
		// error monitoring (ErrorDisplay / Sentry) for investigation.
		return DISABLED_CONFIG;
	}

	if (onboardingStateLoading) {
		return DISABLED_CONFIG;
	}

	const { recommendedConnectors, connectedAppName } = getRecommendedConnectors(
		teamType,
		connectorStates,
	);
	const hasConnectedAny = connectedAppName !== undefined;
	const nextRecommendedConnector = recommendedConnectors[0];

	const onboardingState = deserializeState(onboardingStateData);

	const promoDismissed =
		(onboardingState.promoDismissed as PromoDismissed | undefined) ?? undefined;
	const spotlightShown = !!onboardingState.spotlightShown;

	const enrolmentDateStr = onboardingState.enrolmentDate as string | undefined;
	const enrolmentDate = enrolmentDateStr ? new Date(enrolmentDateStr) : null;
	const daysSinceEnrolment = enrolmentDate
		? Math.floor((Date.now() - enrolmentDate.getTime()) / (1000 * 60 * 60 * 24))
		: 0;

	const sidebarDismissed = promoDismissed === 'sidebar' || promoDismissed === 'both';
	const bannerDismissed = promoDismissed === 'banner' || promoDismissed === 'both';

	return {
		recommendedConnectors,
		connectedAppName,
		nextRecommendedConnector,
		spotlightShown,
		enrolmentDate,
		promoDismissed,
		shouldShowEmptyState: !hasConnectedAny && !hasRecentItems,
		shouldShowNthPromo: hasRecentItems && !hasConnectedAny && !bannerDismissed,
		shouldShowNthPagePromo: hasRecentItems && !hasConnectedAny && !bannerDismissed,
		shouldShowPostConnectPromo: hasConnectedAny && !bannerDismissed,
		shouldShowPostConnectPagePromo: hasConnectedAny && !bannerDismissed,
		shouldShowSidebarItem: !sidebarDismissed,
		shouldShowSpotlight: !!enrolmentDate && daysSinceEnrolment >= 3 && !spotlightShown,
		onSetupConnector,
		onDismissPromo,
		onMaybeLater,
		onDismissSpotlight,
	};
}
